/**
 * OffersMarketedSection — "מה משווקים?" on /join-club (iter66n).
 *
 * Horizontal carousel of the same offers listed on /offers, rendered in
 * the borderless color-blocked style. Clicking a card opens a detail
 * MODAL — NEVER a page navigation and NEVER an external CTA link —
 * because this section sits inside the primary conversion funnel and
 * any exit would leak the lead. The modal's own CTA is "הצטרפו למועדון"
 * which scrolls to `#checkout` on the same page.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Sparkles, X, Users, Target,
  ArrowLeft, Coins,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const heroImageUrl = (raw) =>
  !raw ? '' : raw.startsWith('http') ? raw : `${API_URL}${raw}`;

const offerInitial = (title) => {
  const clean = (title || '').replace(/[״"'\-—]+/g, ' ').trim();
  return (clean.split(/\s+/)[0] || '?').slice(0, 2);
};

/* Splits long text on double newlines → paragraphs with proper rhythm */
const Paragraphs = ({ text }) =>
  (text || '')
    .split(/\n{2,}|\r\n\r\n/)
    .filter((p) => p.trim())
    .map((p, i) => (
      <p key={i} className="text-[#4A4A4A] text-sm md:text-base leading-[1.9] whitespace-pre-line">
        {p}
      </p>
    ));

/* Builds the effective commissions list for display.
 *   1. Prefer the structured `commissions` array (new schema, up to 3 rows).
 *   2. Fallback to legacy single `commission_label` string so older offers still render.
 *   3. Returns [] when nothing to show → the whole block is hidden. */
const resolveCommissions = (offer) => {
  const raw = Array.isArray(offer?.commissions) ? offer.commissions : [];
  const clean = raw
    .map((c) => ({ type: (c?.type || '').trim(), amount: (c?.amount || '').trim() }))
    .filter((c) => c.type || c.amount)
    .slice(0, 3);
  if (clean.length > 0) return clean;
  const legacy = (offer?.commission_label || '').trim();
  if (legacy) return [{ type: '', amount: legacy }];
  return [];
};


/* ── OFFER CARD (inside the carousel) ── */
const OfferCard = ({ offer, onClick }) => {
  const commissions = resolveCommissions(offer);
  return (
  <button
    type="button"
    onClick={onClick}
    className="text-right group relative bg-white rounded-[1.5rem] hover:shadow-[0_20px_50px_-20px_rgba(197,54,106,0.35)] hover:-translate-y-1 transition-all duration-300 flex flex-col w-[280px] md:w-[320px] shrink-0 snap-center overflow-hidden shadow-sm"
    data-testid={`joinclub-offer-card-${offer.slug}`}
  >
    {/* Cover — hero image OR gradient fallback */}
    {offer.hero_image ? (
      <div className="relative aspect-[1200/630] bg-[#FDF8FA] overflow-hidden">
        <img
          src={heroImageUrl(offer.hero_image)}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
    ) : (
      <div className="relative aspect-[1200/630] bg-gradient-to-br from-[#C5366A] via-[#e55b8a] to-[#ff6b9d] overflow-hidden flex items-center justify-center">
        <span className="text-white/40 text-6xl font-black" aria-hidden>
          {offerInitial(offer.title)}
        </span>
      </div>
    )}

    <div className="p-5 flex-1 flex flex-col text-right">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C5366A]/12 text-[#C5366A] text-[10px] font-black tracking-wide">
          <Sparkles className="w-2.5 h-2.5" /> הצעה בלעדית
        </span>
      </div>
      <h3 className="text-[#2D2D2D] text-base md:text-lg font-black leading-snug mb-1.5 line-clamp-2">
        {offer.title}
      </h3>
      {offer.tagline && (
        <p className="text-[#C5366A] text-xs md:text-sm font-bold mb-3 line-clamp-1">
          {offer.tagline}
        </p>
      )}

      {/* Compact commissions preview — the buying signal on the card itself */}
      {commissions.length > 0 && (
        <div
          className="rounded-xl bg-gradient-to-br from-[#0f0f13] to-[#2a1024] p-3 mb-3 space-y-1.5 shadow-inner"
          data-testid={`joinclub-offer-card-commissions-${offer.slug}`}
        >
          <p className="text-[9px] font-black uppercase tracking-[0.15em] text-[#ff9dc0] flex items-center gap-1 mb-1">
            <Coins className="w-2.5 h-2.5" /> עמלות
          </p>
          {commissions.map((c, i) => (
            <div key={i} className="flex items-baseline justify-between gap-2">
              {c.type ? (
                <span className="text-[10px] font-bold uppercase tracking-wide text-white/60 truncate">
                  {c.type}
                </span>
              ) : <span />}
              <span className="text-sm md:text-base font-black text-white whitespace-nowrap">
                {c.amount || '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-auto pt-1 flex items-center justify-between text-xs">
        <span className="text-[#C5366A] font-bold group-hover:underline">קרא עוד ←</span>
        {offer.avatar_age && (
          <span className="text-[#999] flex items-center gap-1">
            <Users className="w-3 h-3" /> {offer.avatar_age}
          </span>
        )}
      </div>
    </div>
  </button>
  );
};


/* ── OFFER DETAIL MODAL — click to expand, no external links ── */
const OfferDetailModal = ({ offer, onClose, onConvert, trialCfg }) => {
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  if (!offer) return null;

  const stopBubbling = (e) => e.stopPropagation();

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-6"
      onClick={onClose}
      data-testid="joinclub-offer-modal"
      dir="rtl"
    >
      <div
        className="bg-white rounded-[1.5rem] md:rounded-[2rem] w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={stopBubbling}
      >
        {/* Sticky header — close button + tiny badge stack */}
        <div className="relative flex-shrink-0">
          {offer.hero_image ? (
            <div className="relative aspect-[1200/500] bg-[#FDF8FA] overflow-hidden">
              <img
                src={heroImageUrl(offer.hero_image)}
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 right-0 p-5 md:p-7 text-right text-white">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-[#C5366A] text-[11px] font-black">
                    <Sparkles className="w-3 h-3" /> הצעה בלעדית
                  </span>
                </div>
                <h3 className="text-xl md:text-3xl font-black leading-tight" data-testid="joinclub-offer-modal-title">
                  {offer.title}
                </h3>
                {offer.tagline && (
                  <p className="text-[#ff9dc0] text-sm md:text-base font-bold mt-1">{offer.tagline}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-[#C5366A] to-[#e55b8a] text-white p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-[#C5366A] text-[11px] font-black">
                  <Sparkles className="w-3 h-3" /> הצעה בלעדית
                </span>
              </div>
              <h3 className="text-xl md:text-3xl font-black leading-tight" data-testid="joinclub-offer-modal-title">
                {offer.title}
              </h3>
              {offer.tagline && (
                <p className="text-white/90 text-sm md:text-base font-bold mt-1">{offer.tagline}</p>
              )}
            </div>
          )}

          <button
            onClick={onClose}
            className="absolute top-3 left-3 w-10 h-10 rounded-full bg-white/95 hover:bg-white text-[#2D2D2D] flex items-center justify-center shadow-md transition-transform hover:scale-110"
            aria-label="סגור"
            data-testid="joinclub-offer-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body — INTENTIONALLY minimal (iter66o):
            The popup lives inside the primary /join-club funnel, so we
            surface ONLY the 3 signals that move the "should I join?"
            decision: (1) commissions (hero), (2) audience, (3) short
            description. Product deep-dive, rules & what-works were
            removed — they belong on the full /offers/{slug} page which
            members can browse after joining. */}
        <div className="flex-1 overflow-y-auto p-5 md:p-8 space-y-6">
          {(() => {
            const commissions = resolveCommissions(offer);
            if (commissions.length === 0) return null;
            return (
              <div
                className="rounded-3xl p-5 md:p-7 bg-gradient-to-br from-[#0f0f13] via-[#1a1520] to-[#2a1024] text-white shadow-[0_20px_50px_-15px_rgba(197,54,106,0.55)] relative overflow-hidden"
                data-testid="joinclub-offer-modal-commissions"
              >
                {/* subtle grain / spotlight */}
                <div
                  aria-hidden
                  className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#C5366A]/25 blur-3xl pointer-events-none"
                />
                <div className="relative">
                  <p className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-[#ff9dc0] mb-3 flex items-center gap-2">
                    <Coins className="w-3.5 h-3.5" /> העמלות שלכם על ההצעה
                  </p>
                  <div className={`grid gap-3 ${commissions.length === 1 ? 'grid-cols-1' : commissions.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
                    {commissions.map((c, i) => (
                      <div
                        key={i}
                        className="rounded-2xl bg-white/8 backdrop-blur-sm border border-white/10 p-4 md:p-5 hover:bg-white/12 transition-colors"
                        data-testid={`joinclub-offer-modal-commission-${i}`}
                      >
                        {c.type && (
                          <p className="text-[10px] md:text-[11px] font-black uppercase tracking-widest text-[#ff9dc0] mb-2">
                            {c.type}
                          </p>
                        )}
                        <p className="text-2xl md:text-4xl font-black leading-none tracking-tight">
                          {c.amount || '—'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Short description — hero_intro only, no product deep-dive */}
          {offer.hero_intro && (
            <div className="space-y-3">
              <p className="text-[10px] md:text-xs font-black uppercase tracking-widest text-[#C5366A] flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> תיאור קצר
              </p>
              <Paragraphs text={offer.hero_intro} />
            </div>
          )}

          {/* Audience block — who is this for */}
          {(offer.avatar_age || offer.avatar_views || offer.avatar_pain) && (
            <div className="bg-[#FDF8FA] rounded-2xl p-5 md:p-6" data-testid="joinclub-offer-modal-audience">
              <p className="text-[10px] md:text-xs font-black uppercase tracking-widest text-[#C5366A] mb-3 flex items-center gap-2">
                <Target className="w-3.5 h-3.5" /> קהלי היעד של ההצעה
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                {offer.avatar_age && (
                  <div>
                    <p className="text-[10px] text-[#999] font-bold uppercase mb-1">גיל</p>
                    <p className="text-[#2D2D2D] font-bold">{offer.avatar_age}</p>
                  </div>
                )}
                {offer.avatar_views && (
                  <div>
                    <p className="text-[10px] text-[#999] font-bold uppercase mb-1">השקפה</p>
                    <p className="text-[#2D2D2D] font-bold">{offer.avatar_views}</p>
                  </div>
                )}
                {offer.avatar_pain && (
                  <div>
                    <p className="text-[10px] text-[#999] font-bold uppercase mb-1">כאב עיקרי</p>
                    <p className="text-[#2D2D2D] font-bold">{offer.avatar_pain}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer CTA — dynamic based on trial config (iter66o.2).
            Mirrors the hero trial card logic on /join-club so the popup
            reflects the SAME live offer (paid trial / free trial / direct
            join) — never a stale hard-coded CTA. */}
        <div className="flex-shrink-0 border-t border-[#F0E4E9] bg-[#FDF8FA] p-4 md:p-5">
          <button
            onClick={onConvert}
            className="w-full group inline-flex items-center justify-center gap-2 bg-gradient-to-l from-[#C5366A] to-[#e84393] text-white py-4 rounded-2xl font-black text-sm md:text-base shadow-[0_10px_30px_-8px_rgba(197,54,106,0.6)] hover:shadow-[0_14px_40px_-8px_rgba(197,54,106,0.85)] hover:-translate-y-0.5 transition-all"
            data-testid="joinclub-offer-modal-cta"
          >
            <span>
              {trialCfg?.mode === 'paid'
                ? `👉 התחל את תקופת הניסיון ב־₪${trialCfg.price_incl_vat} עכשיו`
                : trialCfg?.mode === 'free'
                ? '👉 התחל את הניסיון החינמי עכשיו'
                : '👉 הצטרפו למועדון וקבלו את הנוסחה להצלחה!'}
            </span>
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          </button>
          <p className="text-[11px] text-[#6B6B6B] text-center mt-2 leading-relaxed">
            במועדון מקבלים תכניות פעולה מדוייקות להקמת נכסים מותאמים להצעות למקסימום הכנסה
          </p>
        </div>
      </div>
    </div>
  );
};


/* ── MAIN SECTION — carousel + modal wiring ── */
export const OffersMarketedSection = ({ scrollToCheckout, trialCfg, onOpenTrial }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openOffer, setOpenOffer] = useState(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_URL}/api/promo-offers`)
      .then((r) => (r.ok ? r.json() : { offers: [] }))
      .then((d) => { if (alive) setOffers(d.offers || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Guard: hide the entire section when nothing is published
  if (!loading && offers.length === 0) return null;

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    // RTL scroll — subtract width to move right (forward in reading order)
    const step = scrollerRef.current.clientWidth * 0.85;
    scrollerRef.current.scrollBy({ left: dir === 'next' ? -step : step, behavior: 'smooth' });
  };

  const handleConvert = () => {
    setOpenOffer(null);
    // Preferred path: open the trial signup modal (mirrors the hero CTA
    // behavior). Falls back to scrollToCheckout for legacy callers.
    if (trialCfg && onOpenTrial) {
      onOpenTrial();
    } else if (scrollToCheckout) {
      scrollToCheckout();
    }
  };

  return (
    <section className="py-16 md:py-24 bg-[#FDF8FA]" data-testid="offers-marketed-section">
      <div className="max-w-6xl mx-auto px-5 md:px-6" dir="rtl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
          <div>
            <p className="text-[11px] md:text-xs font-black uppercase tracking-widest text-[#C5366A] mb-2">
              דוגמאות מהמאגר
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-[2.6rem] font-black text-[#2D2D2D] leading-tight" data-testid="offers-marketed-h2">
              מה משווקים?
            </h2>
            <p className="text-[#6B6B6B] text-sm md:text-base leading-relaxed max-w-2xl mt-2">
              הצצה חלקית להצעות VIP שחברי המועדון מקדמים ברגע זה — עמלות גבוהות, קהלים מוכחים ומשפכים שנבדקו בשטח.
            </p>
          </div>
          {offers.length > 2 && (
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={() => scroll('prev')}
                className="w-11 h-11 rounded-full bg-white hover:bg-[#C5366A] hover:text-white text-[#C5366A] shadow-sm flex items-center justify-center transition-colors"
                aria-label="קודם"
                data-testid="offers-marketed-prev"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scroll('next')}
                className="w-11 h-11 rounded-full bg-white hover:bg-[#C5366A] hover:text-white text-[#C5366A] shadow-sm flex items-center justify-center transition-colors"
                aria-label="הבא"
                data-testid="offers-marketed-next"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex gap-4 overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-[280px] md:w-[320px] shrink-0 bg-white rounded-[1.5rem] overflow-hidden shadow-sm">
                <div className="aspect-[1200/630] bg-[#F0E4E9] animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-[#F0E4E9] rounded-full w-1/2 animate-pulse" />
                  <div className="h-5 bg-[#F0E4E9] rounded-full w-4/5 animate-pulse" />
                  <div className="h-3 bg-[#F0E4E9] rounded-full w-full animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={scrollerRef}
            className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 md:-mx-6 px-5 md:px-6 scroll-smooth"
            style={{ scrollbarWidth: 'none' }}
            data-testid="offers-marketed-scroller"
          >
            <style>{`[data-testid="offers-marketed-scroller"]::-webkit-scrollbar { display: none; }`}</style>
            {offers.map((offer) => (
              <OfferCard
                key={offer.id || offer.slug}
                offer={offer}
                onClick={() => setOpenOffer(offer)}
              />
            ))}
          </div>
        )}

        {/* Subtle micro-CTA under the carousel */}
        {!loading && offers.length > 0 && (
          <p className="text-center text-xs md:text-sm text-[#6B6B6B] mt-6 leading-relaxed">
            אלה רק חלק מההצעות — במועדון מחכות לך <strong className="text-[#C5366A]">עשרות הצעות VIP</strong> נוספות עם עמלות גבוהות במיוחד.
          </p>
        )}
      </div>

      {openOffer && (
        <OfferDetailModal
          offer={openOffer}
          onClose={() => setOpenOffer(null)}
          onConvert={handleConvert}
          trialCfg={trialCfg}
        />
      )}
    </section>
  );
};

export default OffersMarketedSection;
